import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { createUseStyles } from 'react-jss';
import { Button, Icon, Typography } from '@packages/ui/shared';
import { AssetViewer } from './asset-viewer/asset-viewer';
import { AssetType } from '@packages/models/api';

export interface IAsset {
    assetType: AssetType | string;
    contentType?: string;
    downloadUrl?: string;
    id: string;
    name: string;
    thumbnailUrl?: string;
    uploadedAt: Date;
}

export interface AssetViewerModalProps {
    assets: IAsset[];
    initialIndex: number;
    onClose: () => void;
    onDownload?: (url: string, filename: string) => void;
}

const useStyles = createUseStyles({
    overlay: {
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        zIndex: 1000,
    },
    closeBtn: {
        position: 'absolute',
        left: 18,
        top: 18,
        zIndex: 1110,
    },
    downloadBtn: {
        position: 'absolute',
        top: 18,
        right: 18,
        zIndex: 1110,
    },
    viewerWrapper: {
        position: 'absolute',
        inset: 0,
        '& .react-viewer-mask': {
            backgroundColor: 'transparent !important',
        },
        '& .react-viewer-canvas': {
            position: 'absolute',
            inset: 0,
        },
        '& .react-viewer-container': {
            position: 'absolute',
            inset: 0,
        },
    },
    navButtons: {
        position: 'absolute',
        bottom: 32,
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 16,
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        borderRadius: 8,
        padding: '12px 24px',
        zIndex: 1110,
    },
    counter: {
        color: 'white',
        fontSize: 14,
        fontWeight: 500,
    },
});

export const AssetViewerModal: React.FC<AssetViewerModalProps> = ({ assets, initialIndex, onClose, onDownload }) => {
    const classes = useStyles();
    const viewerContainerRef = useRef<HTMLDivElement | null>(null);
    const modalRootRef = useRef(document.getElementById('asset-editor-root'));
    const modalElementRef = useRef(document.createElement('div'));
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [viewerReady, setViewerReady] = useState(false);

    const currentAsset = assets[currentIndex];

    const hasMultipleAssets = assets.length > 1;
    const isFirstAsset = currentIndex === 0;
    const isLastAsset = currentIndex === assets.length - 1;

    useEffect(() => {
        const root = modalRootRef.current;
        const element = modalElementRef.current;
        element.className = classes.overlay;
        root?.appendChild(element);

        return () => {
            if (root && root.contains(element)) {
                root.removeChild(element);
            }
        };
    }, [classes.overlay]);

    // Defer viewer mount until the portal container is in the DOM
    useEffect(() => {
        const frame = requestAnimationFrame(() => setViewerReady(true));
        return () => cancelAnimationFrame(frame);
    }, []);

    const goToPrevious = useCallback(() => {
        setCurrentIndex((previousIndex) => Math.max(previousIndex - 1, 0));
    }, []);

    const goToNext = useCallback(() => {
        setCurrentIndex((previousIndex) => Math.min(previousIndex + 1, assets.length - 1));
    }, [assets.length]);

    // Keyboard navigation
    useEffect(() => {
        if (!viewerReady) {
            return undefined;
        }

        function handleKeyDown(event: KeyboardEvent) {
            switch (event.key) {
                case 'Escape':
                    onClose();
                    break;
                case 'ArrowLeft':
                    event.preventDefault();
                    goToPrevious();
                    break;
                case 'ArrowRight':
                    event.preventDefault();
                    goToNext();
                    break;
                default:
                    break;
            }
        }

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [viewerReady, onClose, goToPrevious, goToNext]);

    const handleDownload = useCallback(() => {
        if (!currentAsset) {
            return;
        }

        const source = currentAsset.downloadUrl || currentAsset.thumbnailUrl || '';
        const filename = currentAsset.name || 'attachment';

        if (!source) {
            return;
        }

        if (onDownload) {
            onDownload(source, filename);
            return;
        }

        const link = document.createElement('a');
        link.href = source;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, [currentAsset, onDownload]);

    return ReactDOM.createPortal(
        <>
            <div className={classes.closeBtn}>
                <Button data-testid="asset-viewer-modal-close" variant="link-header-button" onPress={onClose}>
                    <Icon name="x-close" color="white" size={28} />
                </Button>
            </div>

            <div className={classes.downloadBtn}>
                <Button data-testid="asset-viewer-modal-download" variant="link-header-button" onPress={handleDownload}>
                    <Icon name="sync-down" color="white" size={28} />
                </Button>
            </div>

            <div ref={viewerContainerRef} className={classes.viewerWrapper}>
                {viewerReady && currentAsset && (
                    <AssetViewer currentAsset={currentAsset} containerRef={viewerContainerRef.current} />
                )}
            </div>

            {hasMultipleAssets && (
                <div className={classes.navButtons}>
                    <Button
                        data-testid="asset-viewer-modal-previous"
                        disabled={isFirstAsset}
                        onPress={goToPrevious}
                        variant="secondary"
                    >
                        <Icon name="chevron-left" size={20} color="white" />
                    </Button>

                    <Typography style={{ color: 'white', fontSize: 14, fontWeight: 500 }}>
                        Attachment {currentIndex + 1} of {assets.length}
                    </Typography>

                    <Button
                        data-testid="asset-viewer-modal-next"
                        disabled={isLastAsset}
                        onPress={goToNext}
                        variant="secondary"
                    >
                        <Icon name="chevron-right" size={20} color="white" />
                    </Button>
                </div>
            )}
        </>,
        modalElementRef.current
    );
};
